/**
 * MileIQ Scripts
// Usage examples:
// Example 1: Preferable way to use the plugin
MiQ.Experiments.ready(function() {
  console.log('Plugin is ready via callback!');
});
// Example 2: Using the ready event
document.addEventListener('MiQ:Experiments:ready', function(e) {
  console.log('Plugin is ready!', e.detail.instance);
  MiQ.exec('doSomething', 'event listener example');
});
// Example 3: Queueing commands before ready
MiQ.Experiments.exec('doSomething', 'queued command');
// Example 4: Checking if ready before executing
function myFunction() {
  if (MiQ.Experiments.isReady()) {
    MiQ.Experiments.exec('processData', { key: 'value' }, function(result) {
      console.log('Processed:', result);
    });
  } else {
    // Try again later or use the ready method
    MiQ.Experiments.ready(function() {
      this.processData({ key: 'value' }, function(result) {
        console.log('Processed:', result);
      });
    });
  }
}
*/

export default (function (window, document, parent, moduleName) {
  "use strict";

  // Store the previous instance if it exists
  var previousModule = parent[moduleName];

  // Command queue for calls made before plugin is ready
  var commandQueue = [];
  var isReady = false;
  var readyCallbacks = [];

  // Create the main plugin object
  var Module = function () {
    // If called as function without new, return the instance
    if (!(this instanceof Module)) {
      return getInstance();
    }

    // Plugin initialization logic
    this.version = import.meta.env.PACKAGE_VERSION;
    this.list = new Map();

    document.addEventListener("DOMContentLoaded", () => {
      this.init();
    });
  };

  // Singleton instance
  var instance = null;

  function getInstance() {
    if (!instance) {
      instance = new Module();
    }
    return instance;
  }

  // Core methods
  Module.prototype = {
    init: async function () {
      var self = this;

      self.defaultStyles = `/* This should be placed in the page styles also to avoid FOUC, this is a fallback just in case */
      \nhtml:not([data-flags-ready]) [data-flag-variant]:not([data-flag-loading-state]) { display: none; }\n\n`;
      self.appendStyle();

      // LD and Avo ready
      await Promise.all([
        parent.Flags.readyAsync(),
        parent.Telemetry.readyAsync(),
      ]);

      isReady = true;

      // Process command queue
      while (commandQueue.length > 0) {
        var item = commandQueue.shift();
        self[item.method].apply(self, item.args);
      }

      // Call all registered callbacks
      for (var i = 0; i < readyCallbacks.length; i++) {
        readyCallbacks[i].call(self);
      }

      // Dispatch custom event
      var event;
      if (typeof window.CustomEvent === "function") {
        event = new CustomEvent(`MiQ:${moduleName}:ready`, {
          detail: { instance: self },
        });
      } else {
        // IE11 support
        event = document.createEvent("CustomEvent");
        event.initCustomEvent(`MiQ:${moduleName}:ready`, true, true, {
          instance: self,
        });
      }
      document.dispatchEvent(event);

      // Also dispatch a regular DOM event for frameworks that might be listening
      if (typeof window.Event === "function") {
        document.dispatchEvent(new Event(`MiQ${moduleName}Ready`));
      } else {
        // IE11 support
        var legacyEvent = document.createEvent("Event");
        legacyEvent.initEvent(`MiQ${moduleName}Ready`, true, true);
        document.dispatchEvent(legacyEvent);
      }
    },

    appendStyle: function () {
      const self = this;
      const id = "miqexperiments-variants-css";
      const style =
        document.getElementById(id) ?? document.createElement("style");
      style.textContent = self.defaultStyles;
      style.id = id;
      self.styleTag = style;
      document.head.appendChild(style);
    },

    addExperiment: function ({ flagKey, experimentId, experimentName }) {
      const self = this;
      self.list.set(flagKey, { flagKey, experimentId, experimentName });
    },

    logExperimentViewed: function ({
      flagKey,
      variant,
      experimentId,
      experimentName,
    }) {
      const self = this;
      const inExperiment = variant?.inExperiment;
      const existingExperiment = self.list.get(flagKey);
      const experiment = {
        ...(existingExperiment || {}),
        // TODO: review
        experimentId: experimentId || existingExperiment?.experimentId,
        experimentName: experimentName || existingExperiment?.experimentName,
        inExperiment,
        variant: variant._ldDetails,
      };
      self.list.set(flagKey, experiment);
      if (inExperiment) {
        let variationName = `${variant.value}`;
        if (typeof variant.value === "boolean") {
          variationName = variant.value ? "enabled" : "disabled";
        }
        parent.Telemetry.trackExperimentViewed({
          platform: "Web",
          experimentId: experiment.experimentId,
          experimentName: experiment.experimentName,
          variationId: variant.index,
          variationName: variationName,
        });
      }
    },

    renderExperiments: function (skip) {
      const self = this;
      let cssRules = `${self.defaultStyles}`;
      cssRules += `[data-flag-loading-state] { display: none!important; }\n\n`;
      const defaultVariant = {
        value: "control",
      };
      self.list.forEach(({ flagKey }) => {
        try {
          const variant = skip
            ? defaultVariant
            : parent.Flags.variationDetail(flagKey, null) ?? defaultVariant;
          cssRules += `[data-flag-key="${flagKey}"]:not([data-flag-variant="${variant.value}"]),\n`;
          if (!skip) {
            self.logExperimentViewed({ flagKey, variant });
          }
        } catch (e) {
          logger.error(e);
          throw e;
        }
      });

      // Remove trailing comma from last line
      cssRules = cssRules.replace(/,\n$/, "\n");
      cssRules += `{ display: none!important; }`;
      self.styleTag.textContent = cssRules;
      document.documentElement.setAttribute("data-flags-ready", "true");
    },

    runExperiments: function (experiments) {
      const self = this;
      const ld = parent.Flags._launchDarkly;
      experiments?.forEach((experiment) => {
        self.addExperiment(experiment);
      });
      if (experiments?.length) {
        if (ld?.getContext?.()) {
          self.renderExperiments();
        }
        const onReady = () => {
          self.renderExperiments();
          ld?.off("ready", onReady);
        };
        ld?.on("ready", onReady);
        ld?.on("change", self.renderExperiments);
        const onFailed = () => {
          self.renderExperiments(true);
          ld?.off("failed", onFailed);
        };
        ld?.on("failed", onFailed);
      }
    },
  };

  // Public API
  var publicAPI = {
    // Check if plugin is ready
    isReady: function () {
      return isReady;
    },

    // Execute when plugin is ready
    ready: function (callback) {
      if (isReady) {
        callback.call(getInstance());
      } else {
        readyCallbacks.push(callback);
      }
      return this;
    },

    readyAsync: function () {
      return new Promise((resolve) => {
        this.ready(resolve);
      });
    },

    // Queue a command or execute immediately if ready
    exec: function (methodName) {
      var args = Array.prototype.slice.call(arguments, 1);
      var instance = getInstance();

      if (!isReady) {
        commandQueue.push({
          method: methodName,
          args: args,
        });
        return null;
      }

      if (typeof instance[methodName] !== "function") {
        throw new Error("Method " + methodName + " not found");
      }

      return instance[methodName].apply(instance, args);
    },

    // Revert global variable to previous value and return this plugin
    noConflict: function () {
      parent[moduleName] = previousModule;
      return this;
    },

    // Get the plugin instance
    getInstance: getInstance,

    get list() {
      return getInstance().list;
    },

    logExperimentViewed: (...args) => {
      const instance = getInstance();
      return instance.logExperimentViewed.call(instance, ...args);
    },
    renderExperiments: (...args) => {
      const instance = getInstance();
      return instance.renderExperiments.call(instance, ...args);
    },
    runExperiments: (...args) => {
      const instance = getInstance();
      return instance.runExperiments.call(instance, ...args);
    },
    addExperiment: (...args) => {
      const instance = getInstance();
      return instance.addExperiment.call(instance, ...args);
    },
  };

  // Expose the public API
  parent[moduleName] = publicAPI;

  // Auto-initialize
  getInstance();

  return publicAPI;
})(window, document, (window.MiQ = window.MiQ || {}), "Experiments");
